import React, { useCallback, useEffect, useRef } from "react";
import classNames from "classnames";
import { useField } from "formik";
import { EditIcon } from "@icons";
import { TransactionRule } from "@common/types/transactionRule";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useUpdateRuleNote } from "@api/rules.api";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { TextArea } from "@design-system/TextArea/TextArea";
import styles from "./RuleForm.module.scss";
import { normalizeNote } from "./note.helpers";

interface RuleNotesFormProps {
    rule?: TransactionRule;
}

export const RuleNotesForm: React.FC<RuleNotesFormProps> = ({ rule }) => {
    const [field, meta] = useField<string>("note");
    const note = field.value;
    const updateRuleNote = useUpdateRuleNote();
    const { toast, error } = useToaster();
    const lastSavedNote = useRef<string | null>(
        normalizeNote(rule?.note ?? ""),
    );

    useEffect(() => {
        lastSavedNote.current = normalizeNote(rule?.note ?? "");
    }, [rule?.id, rule?.note]);

    const normalizedNote = normalizeNote(note);
    const hasNoteChanged = normalizedNote !== lastSavedNote.current;

    const handleSave = useCallback(async () => {
        if (!rule || updateRuleNote.isPending) {
            return;
        }

        if (normalizedNote === lastSavedNote.current) {
            return;
        }

        try {
            await updateRuleNote.mutateAsync({
                workspaceId: rule.workspaceId,
                ruleId: rule.id,
                note: normalizedNote,
            });
            lastSavedNote.current = normalizedNote;
            toast("Note saved");
        } catch {
            error("Failed to save note");
        }
    }, [normalizedNote, rule, updateRuleNote, toast, error]);

    return (
        <section>
            <header className={styles.header}>
                <div className={styles.group}>
                    <aside
                        className={classNames(
                            styles.sectionIcon,
                            styles.sectionIconNotes,
                        )}
                    >
                        <EditIcon className="icon-color-gray-700" />
                    </aside>
                    <span className={styles.sectionTitle}>Notes</span>
                </div>
            </header>

            <main className={styles.content}>
                <div
                    className={classNames(
                        styles.sectionRows,
                        styles.notesSectionRows,
                    )}
                >
                    <TextArea
                        size="xl"
                        rows={5}
                        resizable
                        invalid={Boolean(meta.touched && meta.error)}
                        className={styles.notesTextarea}
                        placeholder="Add context about this rule..."
                        maxLength={5000}
                        {...field}
                    />
                    {rule && hasNoteChanged && (
                        <ButtonWithLoader
                            variant="primary"
                            size="sm"
                            type="button"
                            loading={updateRuleNote.isPending}
                            className={styles.notesSaveButton}
                            onClick={handleSave}
                            disabled={!hasNoteChanged}
                        >
                            Save
                        </ButtonWithLoader>
                    )}
                </div>
            </main>
        </section>
    );
};
